/**
 * Mapping plugin-config API (mapping service, `/plugin-configs`).
 *
 * Mirrors "Phụ lục A: API contract" of MAPPING_CONFIG_API_PLAN.md one to one.
 * Nothing here names a service, a field or an importer: the form, the diff and
 * the import dialog are all driven by `ServiceSchema` and `ConfigDiff`.
 */

export type VersionStatus = "draft" | "active" | "archived";

export interface VersionListItem {
  service: string;
  plugin_id: string;
  version_id: string;
  status: VersionStatus;
  model_name: string;
  created_at: string;
  created_by: string;
  comment: string;
  base_version_id: string | null;
  /** "api" | "import" | "seed" | null */
  source_kind: string | null;
  activated_at: string | null;
  activated_by: string | null;
}

export interface PluginConfigVersion {
  service: string;
  plugin_id: string;
  version_id: string;
  status: VersionStatus;
  revision: number;
  base_version_id: string | null;
  created_at: string;
  created_by: string;
  comment: string;
  source: { kind: string; [k: string]: unknown } | null;
  activated_at: string | null;
  activated_by: string | null;
  /** Plus every body field of the service, keyed as in `json_schema.properties`. */
  [field: string]: unknown;
}

/** The subset of JSON Schema that pydantic's `model_json_schema()` emits, plus the `x-*` keys. */
export interface JSONSchema {
  type?: string | string[];
  title?: string;
  description?: string;
  default?: unknown;
  enum?: unknown[];
  const?: unknown;
  properties?: Record<string, JSONSchema>;
  required?: string[];
  items?: JSONSchema;
  additionalProperties?: boolean | JSONSchema;
  anyOf?: JSONSchema[];
  oneOf?: JSONSchema[];
  allOf?: JSONSchema[];
  $ref?: string;
  $defs?: Record<string, JSONSchema>;
  minimum?: number;
  maximum?: number;
  exclusiveMinimum?: number;
  exclusiveMaximum?: number;
  minLength?: number;
  maxLength?: number;
  minItems?: number;
  maxItems?: number;
  "x-owner"?: "data" | "code";
  "x-group"?: string;
  "x-key"?: string;
  "x-format"?: "prompt" | "json" | "collection" | string;
  [key: string]: unknown;
}

export type ImportMode = "merge" | "replace";

export interface ImporterSpec {
  id: string;
  field: string;
  label: string;
  accept: string[];
  modes: ImportMode[];
}

export interface ServiceSchema {
  service: string;
  json_schema: JSONSchema;
  groups: string[];
  importers: ImporterSpec[];
}

export type CollectionKey = string | number;

export interface ScalarDiff {
  path: string;
  group: string;
  old: unknown;
  new: unknown;
}

export interface TextDiff {
  path: string;
  group: string;
  unified: string;
}

export interface FieldChange {
  path: string;
  old: unknown;
  new: unknown;
}

export interface CollectionDiff {
  path: string;
  group: string;
  key: string;
  added: { key: CollectionKey; item: Record<string, unknown> }[];
  removed: { key: CollectionKey; item: Record<string, unknown> }[];
  changed: { key: CollectionKey; fields: FieldChange[] }[];
}

export type WarningSeverity = "blocking" | "warning" | "info";

export interface ConfigWarning {
  severity: WarningSeverity;
  /** Stable; the i18n key is `mappingConfig.warnings.<code>`. */
  code: string;
  /** e.g. "qa_template[key=17122].domains" */
  path: string | null;
  /** English; the fallback when the code has no translation. */
  message: string;
  params: Record<string, unknown>;
}

export interface ConfigDiff {
  from_version_id: string | null;
  to_version_id: string;
  scalars: ScalarDiff[];
  texts: TextDiff[];
  collections: CollectionDiff[];
  warnings: ConfigWarning[];
}

export interface DraftResponse {
  version: PluginConfigVersion;
  diff: ConfigDiff;
}

export interface ImportReport {
  importer: string;
  file_name: string;
  file_sha256: string;
  mode: ImportMode;
  sheets: {
    name: string;
    rows_read: number;
    items: number;
    skipped: { row: number; reason: string; text: string | null }[];
  }[];
  autofilled: { key: CollectionKey; field: string; value: unknown }[];
}

export interface ImportResponse extends DraftResponse {
  import_report: ImportReport;
}

export interface ServiceEntry {
  service: string;
  /** The active version of each plugin; empty for a service with no config yet. */
  plugins: VersionListItem[];
}

export interface ServiceList {
  items: ServiceEntry[];
}

/** A plugin with an active config, under a service that accepts an extraction batch. */
export interface BatchTargetPlugin {
  plugin_id: string;
  version_id: string;
  model_name: string;
  activated_at: string | null;
}

/** `GET /plugins/batch-targets`: what a doctype's mapping_config may point at. */
export interface BatchTarget {
  service: string;
  plugins: BatchTargetPlugin[];
}

export interface BatchTargetList {
  items: BatchTarget[];
}

export interface VersionPage {
  items: VersionListItem[];
  next_cursor: string | null;
}

/* ---- Changes (A.3) ---- */

export type CollectionOp =
  | { op: "add"; item: Record<string, unknown> }
  | { op: "update"; key: CollectionKey; set: Record<string, unknown> }
  | { op: "upsert"; item: Record<string, unknown> }
  | { op: "remove"; key: CollectionKey };

export interface UnsetChange {
  $unset: true;
}

export type ChangeValue =
  | UnsetChange
  | { $ops: CollectionOp[] }
  | { $replace: unknown[] }
  | unknown;

export type Changes = Record<string, ChangeValue>;

export interface CreateDraftBody {
  base_version_id?: string | null;
  changes: Changes;
  comment: string;
}

export interface PatchDraftBody {
  expected_revision: number;
  changes: Changes;
  comment?: string;
}

export interface ImportDraftBody {
  file: File;
  importer: string;
  mode: ImportMode;
  comment: string;
  base_version_id?: string | null;
}

export interface ActivateBody {
  expected_active_version_id: string | null;
  acknowledged_warnings: string[];
}

/** `against` of the diff endpoint: a keyword or another version id. */
export type DiffAgainst = "active" | "base" | string;

/* ---- Errors (A.4) ---- */

export interface FieldError {
  path: string;
  message: string;
}

export const CONFLICT_ERROR_CODES = [
  "revision_conflict", "not_a_draft", "active_changed", "plugin_config_conflict",
] as const;
